import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MCAPSuspensionPayload } from "../../hooks/useCopilotRuntime";
import { notifyUploadSettled } from "../../lib/analysisBridge";
import { apiUrl } from "../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setActiveWorkflowNode } from "../../store/slices/copilotSlice";
import { addUploadJob } from "../../store/slices/recordingUploadSlice";
import {
  analysisHandledByWorkflow,
  computeOverallState,
  deriveCardView,
  loadPersistedApproval,
  normalizeStatus,
  savePersistedApproval,
  type OverallKind,
  type PersistedApproval,
} from "./mcapUploadState";
import { McapApprovalRequestView } from "./McapApprovalRequestView";
import { McapUploadLiveView } from "./McapUploadLiveView";

interface McapApprovalCardProps {
  /** The suspension payload emitted by the backend workflow. */
  payload: MCAPSuspensionPayload;
}

interface TriggerResponse {
  batch_id: string;
  job_ids: string[];
  status: string;
}

/**
 * HITL container for the MCAP extraction approval flow.
 *
 * Renders either the approval prompt or the live/settled extraction view.
 * The decision is derived (see `deriveCardView`) with the persisted workflow
 * node as the authoritative signal: a re-hydrated conversation whose workflow
 * already advanced past the suspension (upload done, analysis complete, …)
 * never re-renders the approval prompt — even when this browser has no
 * localStorage record of the approval.
 */
export function McapApprovalCard({ payload }: McapApprovalCardProps) {
  const dispatch = useAppDispatch();
  const appToken = useAppSelector((s) => s.appAuthReducer.appToken);
  const activeThreadId = useAppSelector((s) => s.copilotReducer.activeThreadId);
  const activeUploads = useAppSelector(
    (s) => s.recordingUploadReducer.activeUploads,
  );
  const completedRecently = useAppSelector(
    (s) => s.recordingUploadReducer.completedRecently,
  );
  // Persisted workflow node of the active thread (hydrated from the DB when a
  // conversation is opened). After a refresh/revisit the live SSE completion
  // events are gone, so this is the authoritative post-approval signal.
  const workflowNode = useAppSelector(
    (s) => s.copilotReducer.activeWorkflowNode,
  );
  const resolvedThreadId = payload.thread_id || activeThreadId;
  const resolvedPayload = useMemo(
    () =>
      resolvedThreadId === payload.thread_id
        ? payload
        : { ...payload, thread_id: resolvedThreadId },
    [payload, resolvedThreadId],
  );

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Approval granted in this browser (this session or a previous one).
  // Initialized synchronously so the approval prompt never flashes before an
  // effect settles the view. The card is keyed by thread+incident upstream,
  // so a new suspension always remounts with fresh state.
  const [localApproval, setLocalApproval] = useState<PersistedApproval | null>(
    () => loadPersistedApproval(resolvedPayload),
  );

  const view = useMemo(
    () =>
      deriveCardView(
        localApproval,
        workflowNode,
        payload.analysis_present === true,
      ),
    [localApproval, workflowNode, payload.analysis_present],
  );

  const overall = useMemo(
    () =>
      view.mode === "live"
        ? computeOverallState(view.approval, activeUploads, completedRecently)
        : null,
    [view, activeUploads, completedRecently],
  );

  // Keep the localStorage snapshot in sync with the settled status derived
  // from the workflow node, so later restores render instantly.
  useEffect(() => {
    if (view.mode !== "live" || !view.approval.batchId) return;
    const stored = loadPersistedApproval(resolvedPayload);
    if (stored && stored.status !== view.approval.status) {
      savePersistedApproval(resolvedPayload, view.approval);
    }
  }, [view, resolvedPayload]);

  // Re-seed live job tracking for uploads restored mid-flight (reload /
  // thread reopen); terminal batches render from the settled status alone.
  const seededBatchRef = useRef<string | null>(null);
  useEffect(() => {
    if (view.mode !== "live") return;
    const { approval } = view;
    if (approval.jobIds.length === 0) return;
    if (approval.status === "UPLOADED" || approval.status === "FAILED") return;
    if (seededBatchRef.current === approval.batchId) return;
    seededBatchRef.current = approval.batchId;
    approval.jobIds.forEach((jobId) => {
      if (!activeUploads[jobId] && !completedRecently.includes(jobId)) {
        dispatch(
          addUploadJob({
            jobId,
            recordingId: "",
            deviceId: payload.device_id,
            batchId: approval.batchId,
            status: approval.status,
            progress: 0,
            startTime: Date.now(),
          }),
        );
      }
    });
  }, [view, activeUploads, completedRecently, dispatch, payload.device_id]);

  const handleApprove = useCallback(async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const threadId = resolvedThreadId;
      if (!threadId) {
        throw new Error(
          "This approval is not linked to a conversation. Reopen the conversation and retry.",
        );
      }
      const res = await fetch(`${apiUrl}/v2/copilot/edge/request-mcap`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(appToken ? { Authorization: `Bearer ${appToken}` } : {}),
        },
        body: JSON.stringify({
          incident_id: payload.incident_id,
          thread_id: threadId,
        }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => res.statusText);
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as TriggerResponse;
      const status = normalizeStatus(data.status);
      const approval: PersistedApproval = {
        batchId: data.batch_id,
        jobIds: data.job_ids ?? [],
        status,
      };
      // Seed Redux so the recordingStatusUpdate SSE stream drives the live
      // view by job id (queued → uploading → uploaded / failed).
      seededBatchRef.current = approval.batchId;
      approval.jobIds.forEach((jobId) => {
        dispatch(
          addUploadJob({
            jobId,
            recordingId: "",
            deviceId: payload.device_id,
            batchId: approval.batchId,
            status,
            progress: 0,
            startTime: Date.now(),
            reason:
              status === "QUEUED_OFFLINE"
                ? "Device offline — the extraction will start automatically when it reconnects."
                : undefined,
          }),
        );
      });
      savePersistedApproval(resolvedPayload, approval);
      setLocalApproval(approval);
      // The backend has moved the workflow past the suspension; reflect that
      // in Redux so a stale SUSPENDED node can never resurface the prompt.
      dispatch(setActiveWorkflowNode(status));
    } catch (err) {
      setErrorMsg((err as Error).message ?? "Unknown error");
    } finally {
      setSubmitting(false);
    }
  }, [appToken, dispatch, payload, resolvedPayload, resolvedThreadId]);

  // Hand the settled extraction off to the copilot runtime adapter, which is
  // waiting inside the suspended assistant turn to continue with the live
  // deep-analysis stream. Fires once per terminal transition; skipped when
  // the workflow already owns the analysis phase (post-refresh restores).
  const notifiedKindRef = useRef<OverallKind | null>(null);
  useEffect(() => {
    const kind = overall?.kind;
    if (!kind || (kind !== "UPLOADED" && kind !== "FAILED")) return;
    if (notifiedKindRef.current === kind) return;
    notifiedKindRef.current = kind;
    const alreadyAnalyzed =
      payload.analysis_present === true ||
      analysisHandledByWorkflow(workflowNode);
    if (resolvedThreadId && !alreadyAnalyzed) {
      notifyUploadSettled(resolvedThreadId, kind === "UPLOADED");
    }
  }, [overall, resolvedThreadId, payload.analysis_present, workflowNode]);

  if (view.mode === "live" && overall) {
    return (
      <McapUploadLiveView
        payload={resolvedPayload}
        overall={overall}
        workflowNode={workflowNode}
        submitting={submitting}
        onRetry={() => void handleApprove()}
      />
    );
  }

  return (
    <McapApprovalRequestView
      payload={resolvedPayload}
      submitting={submitting}
      errorMsg={errorMsg}
      onApprove={() => void handleApprove()}
    />
  );
}
